// SEKVENSANALYSEN — HOVEDSKJERMEN I ULL.
//
// ══ HVORFOR DENNE ER HOVEDSKJERMEN OG IKKE EN LISTE OVER UTLØSNINGER ════════
// NJORDs hovedskjerm er en kontaktliste, fordi en kystpost ser fartøy: objekter
// som finnes i tid, har kurs og fart, og kan følges. ULL har ikke slike objekter.
// En UGS-utløsning er et PUNKT — «noe passerte HER, klokka DA» — og den bærer
// INGEN retning. En geofon vet ikke hvilken vei skrittet gikk; den vet at bakken
// ristet.
//
// Retning, fart og antall finnes bare i REKKEFØLGEN av utløsninger på flere
// sensorer. Det er en TOLKNING, den skal jegeren gjøre selv, og derfor er den det
// første han ser. En liste over enkeltutløsninger ville vært en liste over
// punkter uten mening — og verre: den ville invitert til den vanligste
// feilslutningen i hele domenet, «sensoren utløste, altså kom det derfra».
//
// ══ DE FIRE REGLENE SKJERMEN ER BYGGET FOR Å HOLDE ═════════════════════════
//
//  1. FART VISES SOM INTERVALL, ALDRI SOM ETT TALL. Kontrakten gir
//     `fartMinMs`/`fartMaksMs`, og bredden ER informasjonen: er intervallet
//     1,4–7,2 m/s, står sensorene for tett for å måle fart. Den naive midtverdien
//     (4,00 m/s) vises i det hele tatt bare inne i kontraktens egen
//     `begrunnelse`, der den står SAMMEN med intervallet.
//
//  2. ER REKKEFØLGEN IKKE BESTEMT, TEGNES INGEN PIL. `retningsutsagn()` i
//     `lib/sekvens.ts` returnerer en diskriminert union nettopp for å gjøre det
//     umulig å hoppe over det tilfellet her.
//
//  3. TVETYDIGHET VISES, IKKE SKJULES. `begrunnelse` og `advarsler` fra
//     kontrakten gjengis ORDRETT — ikke sammendratt, ikke oversatt. Det er
//     jegerens grunnlag for å være uenig med analysen, og en analyse ingen kan
//     være uenig med, blir ikke etterprøvd.
//
//  4. KONFIDENSER SLÅS ALDRI SAMMEN. Tre seismiske utløsninger som hver sier
//     «person 0,45» blir ikke «person 0,9». De kan alle tre være den samme elgen,
//     og elgen gir STERKERE utslag enn en person. Derfor står den sterkeste
//     enkeltklassifiseringen øverst, og alle de andre står som egne rader.
import {
  BRYTERTRAD_DEKNING_M,
  MAKS_KONFIDENS_SEISMISK,
  dekningsradiusM,
  passeringsfartMs,
  posisjonUsikkerhetM,
  type SekvensBein,
  type UgsSekvens,
  type UgsSensor,
  type UgsUtlosning,
} from '@skade/contracts';
import {
  div, felt, formaterAlder, formaterKlokke, formaterSikkerhet, h, knapp, merke, nv, p, sett,
  sikkerhetsband, span, tomt,
} from '@skade/sensorflate';
import {
  BILDEARSAK_NAVN, GRUNNLAG_NAVN, LYDKLASSE_NAVN, LYSFORHOLD_NAVN, MALKLASSE_NAVN,
  POSISJONSMETODE_NAVN, TOMT_BILDE_BETYR, fartsutsagn, formaterAntall, formaterAvstandSlakk,
  formaterFartMs, formaterKursSlakk, formaterSekunderSlakk, formaterVarighet,
} from '../lib/format.js';
import { hendelserITid, kjenteSensorer } from '../lib/lager.js';
import { sekvensoppsummering } from '../lib/oppsummering.js';
import {
  HASTEBAND_NAVN, analyser, kjedeledd, oppdaterVurderingsminne, retningsutsagn, sammendrag,
  type Kandidat, type Kjedeledd, type Klassifisert, type Vurderingsspor,
} from '../lib/sekvens.js';
import { SENSORART_KAN_IKKE, SENSORART_NAVN, ULLFLAGG_NAVN, type Ullflagg, type Ullhendelse } from '../api/typer.js';
import { ART_IKON, ikon } from '../ui/ikoner.js';
import type { Kontekst } from './kontekst.js';

export interface Sekvenssammendrag {
  antall: number;
  akutte: number;
  utenPosisjon: number;
}

/**
 * VIEW-MINNE, med vilje UTENFOR lageret: lageret er broens bilde av verden, og
 * dette er skjermens hukommelse om hva DEN selv har sagt. Rapportens funn var at
 * en avledet vurdering («mot ØST» → «UBESTEMT») endret seg stille mellom to
 * tegninger — riktig regnestykke, men det leses som en bug. Minnet lar kortet
 * si HVA som endret seg, og når.
 */
let vurderingsminne: ReadonlyMap<string, Vurderingsspor> = new Map();

/**
 * Hvilke kort som står med «Vis detaljer» åpen. Hele flata tegnes om hvert
 * sekund, og uten dette ville utvidelsen smelt igjen ett sekund etter at
 * jegeren åpnet den. Nøkkelen er kandidat-id, som er stabil (første utløsning).
 */
const åpneDetaljer = new Set<string>();

export function tegnSekvensanalyse(vert: HTMLElement, ctx: Kontekst): Sekvenssammendrag {
  const t = ctx.tilstand();
  const naa = ctx.naa();
  const sensorer = kjenteSensorer(t);
  const hendelser = hendelserITid(t);
  const bilde = analyser(hendelser, sensorer, naa);
  const sensorKart = new Map(sensorer.map((s) => [s.sensorId, s]));

  // Minnet føres videre FØR kortene tegnes, så endringslinja gjelder DENNE
  // tegningen. Én klokke per tegning gjelder også her (`naa`).
  vurderingsminne = oppdaterVurderingsminne(vurderingsminne, bilde.kandidater, naa);

  if (hendelser.length === 0) {
    sett(
      vert,
      tomt(
        'Ingen utløsninger ennå',
        t.sensorer.length === 0
          ? 'Ingen sensorer er meldt inn heller. Når broen melder de utlagte nodene, kommer de i stolpen til venstre — og utløsningene deres kommer hit, gruppert i sekvenser.'
          : `${t.sensorer.length} node(r) er lagt ut, men ingen har utløst. Det er den forventede tilstanden: en UGS er stille til noe passerer. Stolpen til venstre viser om nodene fortsatt LEVER (hjertebank), som er et annet spørsmål.`,
        { ikon: ikon('sekvens') },
      ),
    );
    return { antall: 0, akutte: 0, utenPosisjon: 0 };
  }

  const rot = div('sek-liste');

  // Posisjonsløse utløsninger FØRST, fordi de er en driftsfeil jegeren må
  // handle på — ikke noe som skal ligge nederst og bli glemt.
  if (bilde.utenPosisjon.length > 0) {
    rot.append(tegnUtenPosisjon(bilde.utenPosisjon, naa));
  }

  bilde.kandidater.forEach((k, i) => {
    rot.append(tegnKandidat(k, i, sensorKart, ctx, naa));
  });

  sett(vert, rot);
  return {
    antall: bilde.kandidater.length,
    akutte: bilde.kandidater.filter((k) => k.haste.band === 'akutt').length,
    utenPosisjon: bilde.utenPosisjon.length,
  };
}

// ── Posisjonsløse utløsninger ───────────────────────────────────────────────

function tegnUtenPosisjon(hendelser: readonly Ullhendelse[], naaMs: number): HTMLElement {
  const rot = div('sek-kort sek-kort-avvik');
  const hode = div('sek-hode');
  hode.append(ikon('varsel'));
  hode.append(h(3, 'sek-tittel', `${hendelser.length} utløsning(er) UTEN kjent sensorposisjon`));
  rot.append(hode);
  rot.append(
    p(
      'sek-brød',
      'Broen har aldri annonsert nodene disse kom fra. Utløsningene er BEHOLDT — en utløsning fra en ukjent node er nettopp '
      + 'noe du må vite om — men de kan IKKE brukes i sekvensanalyse: uten sensorposisjon finnes ingen avstand å regne fart på. '
      + 'Sjekk om noden er feilkonfigurert, eller om noen andre har lagt den ut.',
    ),
  );
  for (const x of hendelser) {
    rot.append(
      nv(
        x.sensorId,
        `${formaterKlokke(x.sisteTsMs)} · ${formaterAlder(Math.max(0, naaMs - x.sisteTsMs))} · måling «${x.utlosning.maling}»`,
      ),
    );
  }
  return rot;
}

// ── Ett kandidatkort ────────────────────────────────────────────────────────

/**
 * Kortets lag, etter UX-rapporten (J3): ØVERST én setning i vanlig språk med
 * det operative, så endringslinja når en vurdering nettopp endret seg, så
 * kontraktens advarsler — og ALT det tekniske (tallrad, hastebidrag, «bein»,
 * «kandidat», utledning) bak «Vis detaljer». Ingenting er fjernet, bare
 * flyttet: advarslene står UTENFOR utvidelsen, for en advarsel bak et klikk er
 * en advarsel som ikke er gitt.
 */
function tegnKandidat(
  k: Kandidat,
  indeks: number,
  sensorKart: ReadonlyMap<string, UgsSensor>,
  ctx: Kontekst,
  naaMs: number,
): HTMLElement {
  const rot = div(`sek-kort sek-kort-${k.haste.band}`);

  // ── Hode: hva krever oppmerksomhet, og når skjedde det ──────────────────
  const hode = div('sek-hode');
  hode.append(ikon('sekvens'));
  hode.append(h(3, 'sek-tittel', `SEK-${String(indeks + 1).padStart(2, '0')}`));
  hode.append(merke(HASTEBAND_NAVN[k.haste.band], `haste-${k.haste.band}`));
  if (k.hendelser.some((x) => x.ovelse)) hode.append(merke('ØVING', 'ovelse'));
  const flagg = k.hendelser.find((x) => x.flagg !== 'ingen')?.flagg;
  if (flagg) hode.append(merke(ULLFLAGG_NAVN[flagg], `flagg-${flagg}`));
  hode.append(div('sek-rom'));
  hode.append(span('sek-klokke', `${formaterKlokke(k.fraMs)} → ${formaterKlokke(k.tilMs)}`));
  rot.append(hode);

  // Én setning i vanlig språk. Den lages av en REN, testet funksjon — en
  // oppsummering som pynter på usikkerheten er verre enn jargongen.
  rot.append(p('sek-oppsum', sekvensoppsummering({
    utlosninger: k.hendelser.length,
    noder: new Set(k.sensorIder).size,
    retning: k.sekvens ? retningsutsagn(k.sekvens) : null,
    alderMs: Math.max(0, naaMs - k.tilMs),
  })));

  // En vurdering som endret seg («mot ØST» → «UBESTEMT») skal SI det, med
  // klokkeslett — ellers leses det som en bug, og tilliten ryker.
  const spor = vurderingsminne.get(k.id);
  if (spor?.sisteEndring) {
    rot.append(p('sek-endring', `${formaterKlokke(spor.sisteEndring.tsMs)} — ${spor.sisteEndring.tekst}`));
  }

  // Kontraktens advarsler ORDRETT, alltid synlige — aldri bak «Vis detaljer».
  if (k.sekvens) for (const a of k.sekvens.advarsler) rot.append(p('sek-advarsel', a));

  // ── Kontrakten nektet: ÉN utløsning er et punkt ─────────────────────────
  if (!k.sekvens) {
    const avvist = div('sek-bolk sek-bolk-avslag');
    avvist.append(bolkhode('Ingen sekvens — dette er ett punkt'));
    // Kontraktens egen ordlyd, ORDRETT. Den forklarer premisset bedre enn noe vi
    // kunne skrevet her, og den er den samme teksten alle andre klienter får.
    avvist.append(p('sek-brød', k.avslag ?? 'Kontrakten kunne ikke analysere kandidaten.'));
    rot.append(avvist);
  }

  rot.append(tegnDetaljer(k, indeks, sensorKart, ctx, naaMs));
  rot.append(tegnHandlinger(k, ctx));
  return rot;
}

/**
 * «Vis detaljer» — alt det tekniske på kortet, i samme rekkefølge som før:
 * teknisk sammendrag, tallrad, hastebidrag, skillegrunn, kjeden, netto,
 * utløsningene og utledningen. Åpen-tilstanden overlever omtegningen via
 * `åpneDetaljer`, nøklet på kandidatens stabile id.
 */
function tegnDetaljer(
  k: Kandidat,
  indeks: number,
  sensorKart: ReadonlyMap<string, UgsSensor>,
  ctx: Kontekst,
  naaMs: number,
): HTMLElement {
  const det = document.createElement('details');
  det.className = 'sek-detaljer';
  det.open = åpneDetaljer.has(k.id);
  det.addEventListener('toggle', () => {
    if (det.open) åpneDetaljer.add(k.id);
    else åpneDetaljer.delete(k.id);
  });
  const sum = document.createElement('summary');
  sum.textContent = 'Vis detaljer';
  det.append(sum);

  const kropp = div('sek-detaljekropp');
  kropp.append(p('sek-sammendrag', sammendrag(k)));

  const tall = div('sek-tallrad');
  tall.append(felt('Siste utløsning', formaterAlder(Math.max(0, naaMs - k.tilMs))));
  tall.append(felt('Varighet', formaterVarighet((k.tilMs - k.fraMs) / 1000)));
  tall.append(felt('Utløsninger', String(k.hendelser.length)));
  tall.append(felt('Noder', String(new Set(k.sensorIder).size)));
  kropp.append(tall);

  // Hastegraden skal kunne etterprøves — ellers sorterer jegeren i hodet i stedet.
  const grunn = div('sek-hastegrunn');
  for (const b of k.haste.bidrag) {
    grunn.append(span('sek-hastebidrag', `${b.navn} (${b.poeng >= 0 ? '+' : ''}${b.poeng})`));
  }
  kropp.append(grunn);

  if (k.skilleGrunn) kropp.append(p('sek-skille', k.skilleGrunn));

  if (k.sekvens) {
    // ── Kjeden: hvilke sensorer, i hvilken rekkefølge ─────────────────────
    kropp.append(tegnKjede(k.sekvens, sensorKart, k.sensorIder));
    // ── NETTO: retning, fart, antall ──────────────────────────────────────
    kropp.append(tegnNetto(k.sekvens, k));
  }

  // ── Hva hver enkelt sensor faktisk målte ────────────────────────────────
  kropp.append(tegnUtlosninger(k, sensorKart, ctx, naaMs));

  // ── Kontraktens utledning, ORDRETT ──────────────────────────────────────
  if (k.sekvens) kropp.append(tegnUtledning(k.sekvens, indeks === 0));

  det.append(kropp);
  return det;
}

// ── Kjeden ──────────────────────────────────────────────────────────────────

/**
 * Kjeden av sensorer, i den rekkefølgen de utløste.
 *
 * Stripa øverst er en TIDSLINJE og ikke et kart: den sier hvem som utløste etter
 * hvem, ingenting om hvor de står. Mellom to noder står en pil BARE når det
 * beinet har bestemt rekkefølge; ellers står «?». Det er den samme regelen som
 * for netto retning, og den må gjelde per bein også — en pil på en tidslinje
 * leses som en retning.
 *
 * Stripa bygges av `kjedeledd()` (rapportens funn 6): samme node to ganger PÅ
 * RAD er ÉN node som utløste på nytt, og vises som ett ledd med «×2» — mens
 * samme node IGJEN etter andre noder er en legitim tilbakevending, som ikke kan
 * slås sammen og derfor merkes. Beinlista under viser fortsatt hvert par.
 */
function tegnKjede(
  s: UgsSekvens,
  sensorKart: ReadonlyMap<string, UgsSensor>,
  sensorIder: readonly string[],
): HTMLElement {
  const rot = div('sek-bolk');
  rot.append(bolkhode(`Kjeden (${s.bein.length} bein)`));

  const stripe = div('sek-stripe');
  const navn = (id: string): string => sensorKart.get(id)?.navn ?? id;
  const boksFor = (l: Kjedeledd): HTMLElement => {
    const a = sensorKart.get(l.sensorId)?.art;
    const boks = span('sek-node');
    if (a) boks.append(ikon(ART_IKON[a]));
    boks.append(span('sek-nodenavn', navn(l.sensorId)));
    if (l.antall > 1) {
      const gjentak = span('sek-nodegjentak', `×${l.antall}`);
      gjentak.title = `${navn(l.sensorId)} utløste ${l.antall} ganger på rad — slått sammen til ett ledd i kjeden`;
      boks.append(gjentak);
    }
    if (l.tilbakevending) {
      const m = merke('TILBAKEVENDING', 'usikker');
      m.title = `${navn(l.sensorId)} har allerede stått i kjeden. Målet kan ha snudd — eller det er flere mål. En UGS kan ikke skille de to.`;
      boks.append(m);
    }
    return boks;
  };

  // Koblingene mellom to LEDD er nøyaktig beina som ikke er `sammeSensor` — et
  // sammenslått gjentak ER et sammeSensor-bein — så de to listene går i takt.
  const ledd = kjedeledd(sensorIder);
  const koblinger = s.bein.filter((b) => !b.sammeSensor);
  ledd.forEach((l, i) => {
    if (i > 0) {
      const b = koblinger[i - 1] as SekvensBein | undefined;
      // Både pila og «ubestemt» er SVG fra ikonsettet, aldri Unicode-glyfer
      // blandet inn mellom ikoner (UX_DESIGNSYSTEM §6). Og pila står BARE der
      // rekkefølgen er bestemt — en pil på en tidslinje leses som en retning.
      // Mangler beinet (skal ikke skje), er «ubestemt» det ærlige valget.
      const sikker = b?.rekkefolgeSikker === true;
      const kobling = span(`sek-kobling${sikker ? '' : ' sek-kobling-usikker'}`);
      kobling.append(ikon(sikker ? 'pil' : 'ubestemt'));
      if (b) {
        kobling.title = sikker
          ? `${navn(b.fraSensorId)} utløste før ${navn(b.tilSensorId)}`
          : `Rekkefølgen mellom ${navn(b.fraSensorId)} og ${navn(b.tilSensorId)} er IKKE bestemt`;
      }
      stripe.append(kobling);
    }
    stripe.append(boksFor(l));
  });
  rot.append(stripe);

  for (const b of s.bein) rot.append(tegnBein(b, sensorKart));
  return rot;
}

function tegnBein(b: SekvensBein, sensorKart: ReadonlyMap<string, UgsSensor>): HTMLElement {
  const navn = (id: string): string => sensorKart.get(id)?.navn ?? id;
  const rot = div(`sek-bein${b.rekkefolgeSikker ? '' : ' sek-bein-usikker'}${b.urimeligFart ? ' sek-bein-umulig' : ''}`);

  const hode = div('sek-beinhode');
  // Ordet «FØR» framfor et piltegn: teksten skal kunne leses opp over samband,
  // og en pil kan ikke sies. Er rekkefølgen ubestemt, står ingen av dem først.
  hode.append(span('sek-beinnavn', b.sammeSensor
    ? `${navn(b.fraSensorId)} — utløst på nytt`
    : b.rekkefolgeSikker
      ? `${navn(b.fraSensorId)} FØR ${navn(b.tilSensorId)}`
      : `${navn(b.fraSensorId)} og ${navn(b.tilSensorId)} — uavklart hvem som var først`));
  if (!b.rekkefolgeSikker) hode.append(merke('REKKEFØLGE IKKE BESTEMT', 'usikker'));
  if (b.urimeligFart) hode.append(merke('FYSISK UMULIG FART', 'umulig'));
  rot.append(hode);

  const rad = div('sek-beintall');
  if (!b.sammeSensor) rad.append(felt('Avstand', formaterAvstandSlakk(b.avstandM, b.avstandSlakkM)));
  rad.append(felt('Tid mellom', formaterSekunderSlakk(b.tidsdiffS, b.tidsslakkS)));
  // Fartsintervallet, ALDRI midtverdien alene. Se regel 1 i filhodet.
  const f = fartsutsagn(b.fartMinMs, b.fartMaksMs);
  rad.append(felt('Fart', f.slag === 'intervall' ? f.tekst : f.slag === 'nedre-grense' ? `≥ ${formaterFartMs(f.minMs)}` : 'Ingen'));
  if (b.kursDeg !== undefined && b.rekkefolgeSikker) {
    rad.append(felt('Kurs', formaterKursSlakk(b.kursDeg, b.kursSlakkDeg)));
  }
  rot.append(rad);

  if (f.slag !== 'intervall') rot.append(p('sek-beinnote', f.tekst));

  // Beinets egne advarsler ORDRETT fra kontrakten. Dette er de setningene som
  // forklarer HVORFOR et tall er som det er, og de skal aldri sammendras.
  for (const a of b.advarsler) rot.append(p('sek-advarsel', a));
  return rot;
}

// ── NETTO ───────────────────────────────────────────────────────────────────

function tegnNetto(s: UgsSekvens, k: Kandidat): HTMLElement {
  const r = retningsutsagn(s);
  const rot = div(`sek-bolk sek-netto sek-netto-${r.slag}`);
  rot.append(bolkhode('Netto: hvilken vei, hvor fort, hvor mange'));

  const retning = div('sek-retning');
  if (r.slag === 'bestemt') {
    // Pil BARE her. Ikonet er `peiling` fra pakken (en retning), og teksten bærer
    // både kurs, slark og himmelretning — sifrene for kartet, ordet for radioen.
    retning.append(ikon('peiling'));
    retning.append(span('sek-retningstall', formaterKursSlakk(r.kursDeg, r.slakkDeg)));
    if (r.grov) retning.append(merke('GROV', 'usikker'));
  } else {
    retning.append(ikon('varsel'));
    retning.append(span('sek-retningstall', 'RETNING IKKE BESTEMT'));
  }
  rot.append(retning);
  rot.append(p('sek-brød', r.tekst));

  const f = fartsutsagn(s.nettoFartMinMs, s.nettoFartMaksMs);
  const tall = div('sek-tallrad');
  tall.append(felt('Netto fart', f.slag === 'ingen' ? 'Ingen' : f.slag === 'nedre-grense' ? `≥ ${formaterFartMs(f.minMs)}` : f.tekst));
  tall.append(felt('Luftlinje', `${Math.round(s.nettoAvstandM)} m`));
  tall.append(felt('Sti (sum bein)', `${Math.round(s.stiLengdeM)} m`));
  tall.append(felt(
    'Antall passerende',
    formaterAntall(s.antallPasserendeAntatt, s.antallPasserendeMin, s.antallPasserendeMaks),
    'antall',
  ));
  rot.append(tall);
  if (f.slag !== 'intervall') rot.append(p('sek-beinnote', f.tekst));

  // Tvetydighet er ikke en feil i dataene — den er en egenskap ved sensortypen,
  // og den skal stå som en setning og ikke som en farge.
  if (s.tvetydig) {
    rot.append(merke('TVETYDIG', 'usikker'));
    rot.append(p(
      'sek-brød',
      `Mer enn én rimelig tolkning finnes. ${s.antallPasserendeMaks} uavhengige mål som utløser i samme rekkefølge gir PRESIS `
      + 'samme datasett som ett mål i bevegelse. En UGS gir tid og sted, aldri målidentitet.',
    ));
  }

  // Den sterkeste enkeltklassifiseringen — aldri en sammenslåing.
  if (k.beste) {
    const b = k.beste.klasse;
    const kl = div('sek-klasse');
    kl.append(span('sek-klassenavn', MALKLASSE_NAVN[b.klasse]));
    kl.append(merke(formaterSikkerhet(b.konfidens), `sikker-${sikkerhetsband(b.konfidens)}`));
    kl.append(span('sek-klassegrunnlag', GRUNNLAG_NAVN[b.grunnlag]));
    rot.append(kl);
    rot.append(p('sek-brød', b.begrunnelse));
    if (b.alternativer.length > 0) {
      rot.append(nv('Andre klasser som passer', b.alternativer.map((a) => MALKLASSE_NAVN[a]).join(', ')));
    }
    if (b.grunnlag !== 'menneske' && b.konfidens >= MAKS_KONFIDENS_SEISMISK) {
      rot.append(p(
        'sek-advarsel',
        `Konfidensen ligger på taket for slutninger uten menneskeøye (${MAKS_KONFIDENS_SEISMISK}). Den kan IKKE bli høyere av flere `
        + 'utløsninger — skal den over, må et menneske ha sett et bilde.',
      ));
    }
  }
  return rot;
}

// ── Hva hver sensor faktisk målte ───────────────────────────────────────────

function tegnUtlosninger(
  k: Kandidat,
  sensorKart: ReadonlyMap<string, UgsSensor>,
  ctx: Kontekst,
  naaMs: number,
): HTMLElement {
  const rot = div('sek-bolk');
  rot.append(bolkhode(`Utløsningene (${k.hendelser.length})`));
  const valgt = ctx.valgt();
  const klassePer = new Map(k.klassifiseringer.map((x) => [x.hendelse.id, x]));

  for (const hendelse of k.hendelser) {
    const sensor = sensorKart.get(hendelse.sensorId) ?? null;
    const rad = document.createElement('button');
    rad.type = 'button';
    rad.className = `sek-utl${valgt?.id === hendelse.id ? ' sek-utl-valgt' : ''}`;
    rad.setAttribute('aria-pressed', String(valgt?.id === hendelse.id));
    rad.addEventListener('click', () => ctx.velg(valgt?.id === hendelse.id ? null : hendelse.id));

    const hode = div('sek-utlhode');
    if (sensor) hode.append(ikon(ART_IKON[sensor.art]));
    hode.append(span('sek-utlnavn', sensor?.navn ?? hendelse.sensorId));
    if (sensor) hode.append(merke(SENSORART_NAVN[sensor.art], `art-${sensor.art}`));
    hode.append(div('sek-rom'));
    hode.append(span('sek-utlklokke', `${formaterKlokke(hendelse.sisteTsMs)} · ${formaterAlder(Math.max(0, naaMs - hendelse.sisteTsMs))}`));
    rad.append(hode);

    // HVA sensoren målte, i sensorens EGNE enheter. Dette er raden.
    rad.append(p('sek-maaling', maalingTekst(hendelse.utlosning)));
    rad.append(nv('Tidsslark (1σ)', `±${(hendelse.utlosning.tidsusikkerhetMs / 1000).toFixed(3).replace('.', ',')} s`));
    if (sensor) {
      rad.append(nv(
        'Posisjon',
        `${POSISJONSMETODE_NAVN[sensor.posisjonMetode]} ±${Math.round(posisjonUsikkerhetM(sensor))} m · målet var innenfor ${Math.round(dekningsradiusM(sensor))} m av noden`,
        sensor.posisjonMetode === 'anslått' ? 'advarsel' : '',
      ));
      // Det viktigste ene feltet på hele skjermen for en enkeltutløsning.
      rad.append(p('sek-kanikke', SENSORART_KAN_IKKE[sensor.art]));
    }

    const kl = klassePer.get(hendelse.id);
    if (kl) rad.append(tegnKlassifisering(kl));
    rot.append(rad);
  }
  return rot;
}

function tegnKlassifisering(x: Klassifisert): HTMLElement {
  const rot = div('sek-utlklasse');
  rot.append(span('sek-klassenavn', MALKLASSE_NAVN[x.klasse.klasse]));
  rot.append(merke(formaterSikkerhet(x.klasse.konfidens), `sikker-${sikkerhetsband(x.klasse.konfidens)}`));
  rot.append(span('sek-klassegrunnlag', GRUNNLAG_NAVN[x.klasse.grunnlag]));
  // Begrunnelsen ORDRETT. En klasse uten begrunnelse er en påstand ingen kan
  // etterprøve — kontrakten sier det selv, og her er stedet den skal stå.
  rot.append(p('sek-brød', x.klasse.begrunnelse));
  return rot;
}

/**
 * Utløsningen i sensorens egne enheter.
 *
 * Bygget på `maling`-unionen og ikke på valgfrie felt, slik at en lyd-utløsning
 * ikke kan vises uten bånd og en bilde-utløsning ikke uten vurderingsstatus.
 * Amplituden bærer ALLTID enheten, og en rå ADC-teller merkes UKALIBRERT: den kan
 * bare sammenlignes med SAMME sensors tidligere utløsninger, aldri mot en terskel.
 */
export function maalingTekst(u: UgsUtlosning): string {
  switch (u.maling) {
    case 'terskel': {
      const deler = [
        `Terskelutløsning: ${u.amplitude} ${u.amplitudeEnhet}${u.amplitudeEnhet === 'rå-teller' ? ' (UKALIBRERT — kun sammenlignbar med samme sensor)' : ''}`,
        `varighet ${(u.varighetMs / 1000).toFixed(2).replace('.', ',')} s`,
      ];
      if (u.terskelverdi !== undefined) deler.push(`terskel ${u.terskelverdi} ${u.amplitudeEnhet}`);
      if (u.skrittrateHz !== undefined) deler.push(`skrittrate ${u.skrittrateHz.toFixed(2).replace('.', ',')} Hz (UTLEDET av omhyllingen, ikke målt)`);
      if (u.dominantFrekvensHz !== undefined) deler.push(`dominant ${Math.round(u.dominantFrekvensHz)} Hz`);
      if (u.amplitudeEnhet === 'binær') deler.push(`brytertråd: dekker bare ~${BRYTERTRAD_DEKNING_M} m på tvers, men posisjonen er den skarpeste vi har`);
      return `${deler.join(' · ')}.`;
    }
    case 'retning': {
      const f = passeringsfartMs(u);
      const deler = [
        `Dobbelstråle: passerte ${u.passering} på akse ${Math.round(u.aksekursDeg)}°${u.aksekursUsikkerhetDeg > 0 ? ` ±${Math.round(u.aksekursUsikkerhetDeg)}°` : ''}`,
      ];
      if (f !== undefined) deler.push(`lokal passeringsfart ${formaterFartMs(f)} (målt ved sensoren, uavhengig av sekvensen)`);
      if (u.antallPasseringer !== undefined) deler.push(`${u.antallPasseringer} passering(er) i samme vindu`);
      deler.push('Retningen er et FORTEGN på en akse, ikke en kurs — sensoren måler ikke grader');
      return `${deler.join(' · ')}.`;
    }
    case 'lyd': {
      const deler = [
        `Lyd: ${LYDKLASSE_NAVN[u.lydklasse]} (nodens egen konfidens ${u.klassekonfidens.toFixed(2)})`,
        `bånd ${Math.round(u.bandFraHz)}–${Math.round(u.bandTilHz)} Hz`,
      ];
      if (u.nivaDbRe20uPa !== undefined) deler.push(`nivå ${u.nivaDbRe20uPa.toFixed(1).replace('.', ',')} dB re 20 µPa (LUFT — ikke marin referanse)`);
      if (u.nivaDbRe20uPa !== undefined && u.stoyGulvDbRe20uPa !== undefined) {
        deler.push(`SNR ${(u.nivaDbRe20uPa - u.stoyGulvDbRe20uPa).toFixed(1).replace('.', ',')} dB (utledet)`);
      }
      if (u.varighetMs !== undefined) deler.push(`varighet ${(u.varighetMs / 1000).toFixed(1).replace('.', ',')} s`);
      return `${deler.join(' · ')}.`;
    }
    case 'bilde': {
      const deler = [
        `Bilde: ${u.antallBilder} stk, id ${u.bildeId}`,
        BILDEARSAK_NAVN[u.utlosningsarsak],
      ];
      if (u.lysforhold) deler.push(LYSFORHOLD_NAVN[u.lysforhold]);
      deler.push(u.vurdertAvMenneske
        ? `SETT av ${u.vurdertAv ?? 'operatør'}${u.menneskeligVurdering ? ` — vurdert som ${MALKLASSE_NAVN[u.menneskeligVurdering]}` : ' — ingen klasse kunne avgjøres'}`
        : 'IKKE SETT av noe menneske ennå');
      deler.push(TOMT_BILDE_BETYR[u.utlosningsarsak]);
      return deler.join(' · ');
    }
  }
}

// ── Utledningen ─────────────────────────────────────────────────────────────

/**
 * Kontraktens `begrunnelse`, linje for linje, ORDRETT.
 *
 * Den øverste kandidaten står åpen og resten lukket. Ingenting er SKJULT — alt er
 * ett klikk unna, og lista over kandidater ville ellers blitt hundrevis av linjer
 * lang før man kom til den andre sekvensen. Advarslene tegnes IKKE her lenger:
 * de står øverst på kortet, utenfor «Vis detaljer» (`tegnKandidat`), fordi en
 * advarsel bak et klikk er en advarsel som ikke er gitt.
 */
function tegnUtledning(s: UgsSekvens, apen: boolean): HTMLElement {
  const rot = div('sek-bolk');

  const det = document.createElement('details');
  det.className = 'sek-utledning';
  det.open = apen;
  const sum = document.createElement('summary');
  sum.textContent = `Utledning fra kontrakten (${s.begrunnelse.length} linjer) — kan etterregnes`;
  det.append(sum);
  const liste = div('sek-utledningslinjer');
  for (const b of s.begrunnelse) liste.append(p('sek-utledningslinje', b));
  det.append(liste);
  rot.append(det);
  return rot;
}

// ── Handlinger ──────────────────────────────────────────────────────────────

/**
 * Hva jegeren kan gjøre med sekvensen.
 *
 * «Lag melding» STRÅLER IKKE i seg selv — den fyller ut meldingsskjermen. Selve
 * sendingen ligger der, bak EMCON-bryteren, og det skillet er bevisst: å skrive
 * en melding og å kringkaste den er to ulike beslutninger, og de skal tas hver
 * for seg.
 */
function tegnHandlinger(k: Kandidat, ctx: Kontekst): HTMLElement {
  const rot = div('sek-bolk sek-handlinger');
  const rad = div('sek-knapperad');
  rad.append(knapp('Lag melding fra denne sekvensen', () => {
    ctx.velgForMelding(k.id);
    ctx.kunngjør('Meldingsutkastet er fylt ut fra sekvensen. Ingenting er sendt.');
  }, { variant: 'primær', ikon: ikon('melding'), tittel: 'Fyller ut meldingsutkastet. Sender ingenting.' }));
  rot.append(rad);

  // Flagget gjelder ALLE utløsningene i kandidaten: det er sekvensen jegeren
  // vurderer, ikke det enkelte skrittet i bakken.
  const flagg = div('sek-flagg');
  flagg.append(span('sek-flaggetikett', 'Min vurdering av sekvensen:'));
  const nå: Ullflagg = k.hendelser.find((x) => x.flagg !== 'ingen')?.flagg ?? 'ingen';
  for (const verdi of ['relevant', 'avskrevet-dyr', 'avskrevet-egen', 'ingen'] as const) {
    const b = knapp(ULLFLAGG_NAVN[verdi], () => {
      for (const x of k.hendelser) ctx.settFlagg(x.id, verdi);
      ctx.kunngjør(`Sekvensen merket «${ULLFLAGG_NAVN[verdi]}»`);
    }, { variant: nå === verdi ? 'primær' : 'stille' });
    b.setAttribute('aria-pressed', nå === verdi ? 'true' : 'false');
    flagg.append(b);
  }
  rot.append(flagg);
  return rot;
}

function bolkhode(tekst: string): HTMLElement {
  return h(3, 'sek-bolkhode', tekst);
}
